import { createFeatureSelector, createSelector } from '@ngrx/store';
import { LoadingState, getError } from 'app/utils/callstate';
import * as _ from 'lodash';
import { PayrollState } from '../payroll.types';
import { RouteState, State, fromPayroll } from '../reducers';
import { PayrollEntry, PayrollEntryByName } from 'app/api/payroll.api.service';
import * as moment from 'moment';
import { selectPayrollRule } from './payroll.rule.selectors';
import { PayrollRuleEntry } from 'app/api/payroll.rule.api.service';
import { selectPayrollExtra } from './payroll.extra.selectors';
import { PayrollExtraEntry } from 'app/api/payroll.extra.api.service';

export const getRouteState = createFeatureSelector<State, RouteState>(
  'payroll'
);

export const selectPayrollState = createSelector(
  getRouteState,
  ({ api }): PayrollState => api
);

export const selectPayroll = createSelector(
  selectPayrollState,
  fromPayroll.selectAll
);

export const selectPayrollCube = createSelector(
  selectPayroll,
  selectPayrollRule,
  (payrolls: PayrollEntry[], rules: PayrollRuleEntry[]) => {
    const ruleGroup = _.groupBy(rules, 'payrollKey');

    return _.map(payrolls, (payroll) => {
      payroll.timeline = moment(payroll.month).format('YYYY-MM');
      payroll.fullRaw =
        payroll.primes + payroll.others + payroll.extra + payroll.raw;
      payroll.fullRawCharges = payroll.fullRaw + payroll.charges;

      payroll.extraAxis = {};
      payroll.ana2Axis = {};

      if (ruleGroup[payroll.matricule]) {
        _.forEach(ruleGroup[payroll.matricule], (rule) => {
          if (payroll.matricule === rule.payrollKey) {
            if (rule.axis === 'extras') {
              payroll.extraAxis[rule.payrollExtraKey] = rule.payrollExtraValue;
            } else if (rule.axis === 'ana2') {
              payroll.ana2Axis[rule.payrollExtraKey] = rule.payrollExtraValue;
            }
          }
        });
        return payroll;
      }
      return payroll;
    });
  }
);

export const selectPayrollByNameUniq = (payrollFilter?: string[]) =>
  createSelector(
    selectPayrollCube,
    selectPayrollRule,
    (payrolls: PayrollEntry[], rules: PayrollRuleEntry[]) => {
      const userMap = {};
      const years = [];
      const totalFlux = {};

      payrolls.forEach((payroll) => {
        const getYear = payroll.timeline.split('-')[0];

        payroll.fullRawChargesRecalculated = payroll.fullRaw + payroll.charges;

        if (years.indexOf(getYear) < 0) {
          years.push(getYear);
          totalFlux[getYear] = 0;
        }

        if (!userMap[payroll.name]) {
          userMap[payroll.name] = [];
        }

        // console.log('Selected index :::', payrollFilter);

        if (payrollFilter && payrollFilter.length > 0) {
          for (const f of payrollFilter) {
            if (payroll[f] && f !== 'fullRawCharges' && f !== 'fullRaw') {
              payroll.fullRawChargesRecalculated -= payroll[f];
            }
          }
        }

        userMap[payroll.name].push({
          name: payroll.name,
          matricule: payroll.matricule,
          timeline: payroll.timeline,
          extras: _.filter(rules, (d) => d.payrollKey === payroll.matricule),
          fullRawChargesRecalculated: payroll.fullRawChargesRecalculated,
          fullRawCharges: payroll.fullRawCharges,
        });
      });

      // console.log(userMap);

      const payrollByUser: any = {};

      Object.keys(userMap).forEach((name: string) => {
        if (!payrollByUser[name]) {
          payrollByUser[name] = {
            name,
            matricule: '',
            items: [],
            extras: [],
            totalFlux: {},
            totalFluxCharges: {}
          };
        }

        userMap[name].forEach(
          ({
            timeline,
            fullRawChargesRecalculated,
            fullRawCharges,
            matricule,
            extras,
          }) => {
            const getYear = timeline.split('-')[0];
            if (!payrollByUser[name].totalFlux[getYear]) {
              payrollByUser[name].totalFlux[getYear] = 0;
            }
            if (!payrollByUser[name].totalFluxCharges[getYear]) {
              payrollByUser[name].totalFluxCharges[getYear] = 0;
            }
            payrollByUser[name].totalFlux[
              getYear
            ] += fullRawChargesRecalculated;
            payrollByUser[name].totalFluxCharges[getYear] += fullRawCharges;
            payrollByUser[name].matricule = matricule;
            payrollByUser[name].extras = extras;
          }
        );

        payrollByUser[name].items = userMap[name];
      });

      // console.log(payrollByUser);

      return Object.values(payrollByUser) as PayrollEntryByName[];
    }
  );

export const selectPayrollByName = (name: string) =>
  createSelector(selectPayroll, (payrolls: PayrollEntry[]) =>
    _.filter(payrolls, (data) => data.name === name)
  );

export const selectPayrollExtraFormatted = createSelector(
  selectPayrollExtra,
  selectPayrollRule,
  (extras: PayrollExtraEntry[], rules: PayrollRuleEntry[]) => {
    const extrasMap = {};

    _.forEach(extras, (extra) => {
      if (!extra.combobox) {
        if (!extra.choices) {
          extrasMap[extra.name] = [];
        } else {
          extrasMap[extra.name] = extra.choices.split('|');
        }
      }
    });

    _.forEach(rules, (rule) => {
      if (extrasMap[rule.payrollExtraKey]) {
        if (
          extrasMap[rule.payrollExtraKey].indexOf(rule.payrollExtraValue) < 0
        ) {
          extrasMap[rule.payrollExtraKey].push(rule.payrollExtraValue);
        }
      }
    });

    if (extras) {
      return _.map(extras, (extra) => {
        if (extra) {
          if (!extra.choices && extrasMap[extra.name]) {
            extra.choices = extrasMap[extra.name].join('|') || '';
          } else if (extrasMap[extra.name]) {
            extra.choices = extrasMap[extra.name].join('|') || '';
          }
          if (extra.choices) {
            return {
              name: extra.name,
              choices: extra.choices.split('|'),
            };
          }
        }
        return null;
      });
    }
    return null;
  }
);

export const selectPayrollExtraByName = (name: string) =>
  createSelector(selectPayrollExtraFormatted, (extras: any[]) => {
    if (extras && name) {
      return _.filter(extras, (extra) => extra && extra.name === name)[0];
    }
    return null;
  });

export const selectPayrollLoading = createSelector(
  selectPayrollState,
  (payrollState) => {
    return payrollState.callState === LoadingState.Loading;
  }
);

export const selectPayrollLoaded = createSelector(
  selectPayrollState,
  (payrollState) => payrollState.callState === LoadingState.Loaded
);

export const selectPayrollError = createSelector(
  selectPayrollState,
  (payrollState) => getError(payrollState.callState)
);
